/** Small shared helpers for node-type semantic linters. */
import { nodePathLiteral } from '../godot/variant/variantParser.js';
import { descendsFromClass } from '../godot/classdb/classBaseTypes.js';
import { perObject } from '../utils/perObject.js';
function buildSceneIndex(roots) {
    const parentOf = new Map();
    const underInstanceAncestor = new Set();
    const nodesByType = new Map();
    const walk = (nodes, parent, ancestorIsInstance) => {
        for (const node of nodes) {
            parentOf.set(node, parent);
            const ofType = nodesByType.get(node.type);
            if (ofType)
                ofType.push(node);
            else
                nodesByType.set(node.type, [node]);
            if (ancestorIsInstance)
                underInstanceAncestor.add(node);
            walk(node.children, node, ancestorIsInstance || Boolean(node.instance));
        }
    };
    walk(roots, null, false);
    // Freeze each bucket: `nodesOfType` hands these arrays straight to callers with no
    // per-call copy, matching `RuleRegistry`'s hot-path contract, and the freeze enforces it.
    for (const ofType of nodesByType.values())
        Object.freeze(ofType);
    return { parentOf, underInstanceAncestor, nodesByType };
}
const getSceneIndex = perObject(buildSceneIndex);
/** Shared empty result for a type miss: one frozen instance, not an allocation per miss. */
const NO_MATCHES = Object.freeze([]);
/**
 * Every node of `type`, in depth-first (Godot tree) order, frozen, not copied. A rule
 * walking the tree in `check` for an "only the first has an effect" warning costs
 * O(matches x nodes); this, {@link countNodesOfType} and {@link firstNodeOfType} read the
 * cached index. Exact-name: Godot compares `get_class()` or scans a type-keyed group.
 */
export function nodesOfType(roots, type) {
    return getSceneIndex(roots).nodesByType.get(type) ?? NO_MATCHES;
}
/**
 * Every node whose class is `base` or inherits it, in depth-first order, frozen, off the same
 * cached index: {@link nodesOfType} by ancestry, for a rule that asks what a node is rather than
 * what it is named. A class the catalog does not know inherits nothing. Each call walks the tree
 * once, so a rule that asks per node caches the answer per tree, as the audio checks do.
 */
export function nodesDescendingFrom(roots, base) {
    const index = getSceneIndex(roots);
    // One ancestry test per distinct class, not per node: a scene repeats its classes.
    const heirClasses = new Set();
    for (const type of index.nodesByType.keys()) {
        if (descendsFromClass(type, base))
            heirClasses.add(type);
    }
    if (heirClasses.size === 0)
        return NO_MATCHES;
    const heirs = [];
    for (const node of index.parentOf.keys()) {
        if (heirClasses.has(node.type))
            heirs.push(node);
    }
    return Object.freeze(heirs);
}
/** How many nodes of `type` the scene contains, off the same cached index. */
export function countNodesOfType(roots, type) {
    return nodesOfType(roots, type).length;
}
/**
 * The first node of `type` in depth-first order, the one `SceneTree::get_first_node_in_group`
 * returns: `_update_group_order` (`scene_tree.cpp:333-347`) sorts by `Node::Comparator`
 * (`node.h:132-134`), tree order. Exact-name, like `countNodesOfType`, since groups are
 * keyed by concrete class.
 */
export function firstNodeOfType(roots, type, joins) {
    const ofType = getSceneIndex(roots).nodesByType.get(type);
    if (!ofType)
        return null;
    // `joins` keeps the nodes that enter the group: `WorldEnvironment` joins only
    // `if (environment.is_valid())` (world_environment.cpp:39-40). It scans this type's
    // cached nodes, where a tree walk would be quadratic when the type repeats.
    return (joins ? ofType.find(joins) : ofType[0]) ?? null;
}
/**
 * The parent of `target`, or null for a root or an absent node, in O(1) from the
 * cached index. Outside tests only `parentType.ts` calls it, and every rule asks `parentType.ts`.
 */
export function findParentNode(nodes, target) {
    return getSceneIndex(nodes).parentOf.get(target) ?? null;
}
/**
 * Extract the inner path from a `NodePath("...")` literal, or null when `value`
 * is not a NodePath literal or wraps an empty path. Shared by the NodePath-target
 * linters that resolve a node reference before checking its type.
 */
export function extractNodePath(value) {
    // Stricter than `nodePathLiteral` on one point only: an empty path is "no
    // path" to a rule resolving a reference, where a display formatter still has
    // an empty string to show.
    return nodePathLiteral(value) || null;
}
/**
 * True when `target` has an ancestor that is an instanced sub-scene (`instance=`).
 * Such a node's NodePath references can resolve into sub-scene internals the
 * static linter never sees, so not-found / wrong-type assertions are unsafe.
 * Answered from the cached scene index in O(1).
 */
export function isUnderInstance(roots, target) {
    return getSceneIndex(roots).underInstanceAncestor.has(target);
}
//# sourceMappingURL=linterUtils.js.map